import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Check, ExternalLink, FileText, Plus, Search, ChevronLeft, ChevronRight, Database, X, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { motion, AnimatePresence } from 'motion/react';
import { dataroomApi } from '../../../api/dataroomApi';
import { dataSourcesApi } from '../../../api/dataSourcesApi';
import { formatModeratorApiKeyError, getModeratorApiKey } from '../../../utils/apiClient';
import {
  openImportDocument,
  isImportDocumentLink,
  parseImportDocumentId,
} from '../../../utils/importDocumentDownload';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import { addArticleToDataroom, dedupeStrings, loadDataroom, subscribeDataroom } from '../../state/dataroomStore';
import { TopicTagsOverflow } from '../../components/TopicTagsOverflow';

interface RawItem {
  id: string;
  title: string;
  source: string;
  collectedAt: Date;
  topics: string[];
  url?: string;
  companyNames: string[];
}

const TOPIC_COLORS: Record<string, string> = {
  AI:            'bg-blue-100 text-blue-700',
  Finance:       'bg-amber-100 text-amber-700',
  Cloud:         'bg-sky-100 text-sky-700',
  Blockchain:    'bg-orange-100 text-orange-700',
  Cybersecurity: 'bg-red-100 text-red-700',
  Tech:          'bg-violet-100 text-violet-700',
};

function toDateKey(dt: Date) {
  return dt.toISOString().slice(0, 10);
}

function formatDateLabel(
  dateKey: string,
  todayKey: string,
  yesterdayKey: string,
  t: (key: string) => string,
  localeTag: string,
) {
  if (dateKey === todayKey) return t('common.today');
  if (dateKey === yesterdayKey) return t('common.yesterday');
  const dt = new Date(dateKey + 'T00:00:00');
  return dt.toLocaleDateString(localeTag, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(dt: Date, localeTag: string) {
  return dt.toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit' });
}

function articleHref(row: RawItem): string {
  if (row.url) return row.url.startsWith('http') ? row.url : `https://${row.url}`;
  return row.source;
}

function topicsFromApiRow(
  r: { topic_names?: string[]; topic?: string | null },
  uncategorized: string,
): string[] {
  if (Array.isArray(r.topic_names) && r.topic_names.length > 0) {
    return dedupeStrings(r.topic_names);
  }
  if (r.topic?.trim()) return [r.topic.trim()];
  return [uncategorized];
}

function topicChipClass(topic: string): string {
  return `inline-block max-w-[6.5rem] truncate text-[11px] font-medium px-2 py-0.5 rounded-full ${TOPIC_COLORS[topic] ?? 'bg-slate-100 text-slate-600'}`;
}

export function CollectedRawPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast() as {
    showSuccess: (msg: string) => void;
    showError: (msg: string) => void;
  };
  const { t, localeTag } = useUiLocale();
  const now = new Date();
  const todayKey     = toDateKey(now);
  const yesterdayKey = toDateKey(new Date(now.getTime() - 86_400_000));

  const [dateKeys, setDateKeys] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [apiItems, setApiItems] = useState<RawItem[]>([]);
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  /* ── Load available dates on mount ── */
  useEffect(() => {
    const ac = new AbortController();
    setIsLoadingDates(true);
    dataSourcesApi.listDates({ signal: ac.signal })
      .then(({ data }) => {
        const dates: string[] = Array.isArray(data)
          ? (data as { date: string }[]).map((d) => d.date).sort((a, b) => b.localeCompare(a))
          : [];
        setDateKeys(dates);
        if (dates.length > 0) setSelectedDate(dates[0]);
      })
      .catch(() => { /* stay empty */ })
      .finally(() => setIsLoadingDates(false));
    return () => ac.abort();
  }, []);

  /* ── Load items when date changes ── */
  const itemsAcRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!selectedDate) return;
    itemsAcRef.current?.abort();
    const ac = new AbortController();
    itemsAcRef.current = ac;
    setIsLoadingItems(true);
    dataSourcesApi.list({ date: selectedDate, limit: 500 }, { signal: ac.signal })
      .then(({ data }) => {
        if (ac.signal.aborted) return;
        const raw = Array.isArray(data) ? data : [];
        setApiItems(
          raw.map(
            (r: {
              id: number;
              title: string;
              source: string;
              scraped_at: string;
              topic?: string | null;
              topic_names?: string[];
              url?: string;
              link?: string;
              company_names?: string[];
            }) => {
              const articleUrl = r.url ?? r.link ?? '';
              const uncategorized = t('common.uncategorized');
              return {
                id: String(r.id),
                title: r.title,
                source: r.source,
                collectedAt: new Date(r.scraped_at),
                topics: topicsFromApiRow(r, uncategorized),
                url: articleUrl || undefined,
                companyNames: r.company_names ?? [],
              };
            },
          ),
        );
      })
      .catch(() => { if (!ac.signal.aborted) setApiItems([]); })
      .finally(() => { if (!ac.signal.aborted) setIsLoadingItems(false); });
    return () => ac.abort();
  }, [selectedDate, t]);

  /* ── Active item set ── */
  const activeItems: RawItem[] = apiItems;

  const uncategorizedLabel = t('common.uncategorized');

  const allTopics = useMemo(
    () =>
      [...new Set(activeItems.flatMap((i) => i.topics))]
        .filter((topic) => topic !== uncategorizedLabel)
        .sort(),
    [activeItems, uncategorizedLabel],
  );

  const [search, setSearch]             = useState('');
  const [topicFilter, setTopicFilter]   = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dataroomIds, setDataroomIds] = useState<Set<number>>(() => new Set());

  const searchWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const syncDataroomIds = () => {
      const ids = loadDataroom().articles.map((article) => article.articleId);
      setDataroomIds(new Set(ids));
    };
    syncDataroomIds();
    return subscribeDataroom(syncDataroomIds);
  }, []);

  const handleAddToDataroom = useCallback(async (row: RawItem) => {
    const articleId = Number(row.id);
    if (!Number.isFinite(articleId)) return;

    if (!getModeratorApiKey()) {
      showError(t('dataPool.toastApiKey'));
      return;
    }

    try {
      await dataroomApi.addArticle(articleId);
      addArticleToDataroom({
        articleId,
        title: row.title || t('common.articleNumber', { id: articleId }),
        url: articleHref(row),
        summary: '',
        topics: dedupeStrings(row.topics.filter((topic) => topic !== uncategorizedLabel)),
        companies: dedupeStrings(row.companyNames),
        addedAt: new Date().toISOString(),
      });
      showSuccess(t('dataPool.toastAdded'));
    } catch (err: unknown) {
      const detail = formatModeratorApiKeyError(err);
      if (detail.toLowerCase().includes('has not been analyzed')) {
        showError(t('dataPool.toastNeedAnalysis'));
        return;
      }
      if (detail.toLowerCase().includes('article not found')) {
        showError(t('dataPool.toastNotFound'));
        return;
      }
      showError(detail);
    }
  }, [showSuccess, showError, t, uncategorizedLabel]);

  /* Close topic dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Items for selected date, filtered ── */
  const dateItems = activeItems;

  const displayed = useMemo(() => {
    let items = activeItems;
    if (topicFilter) items = items.filter((i) => i.topics.includes(topicFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.source.toLowerCase().includes(q) ||
          (i.url && i.url.toLowerCase().includes(q)) ||
          i.topics.some((topic) => topic.toLowerCase().includes(q)) ||
          i.companyNames.some((c) => c.toLowerCase().includes(q)),
      );
    }
    return items.sort((a, b) => a.collectedAt.getTime() - b.collectedAt.getTime());
  }, [activeItems, topicFilter, search]);

  /* ── Date nav (only through dates that have articles) ── */
  const currentIdx  = dateKeys.indexOf(selectedDate);
  const canGoOlder  = currentIdx < dateKeys.length - 1;
  const canGoNewer  = currentIdx > 0;

  const handleDateInput = (value: string) => {
    if (value) setSelectedDate(value);
  };

  const selectTopic = (topic: string) => {
    setTopicFilter((prev) => (prev === topic ? null : topic));
    setDropdownOpen(false);
  };

  return (
    <div className="hawk-admin-surface flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Header */}
      <header className="hawk-glass-header hawk-glass-header--login shrink-0">
        <div className="px-1 py-2 sm:py-2.5">
          <div className="flex w-full min-w-0 items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                <span className="hawk-gradient-text">{t('dataPool.title')}</span>
              </h1>
              <p className="hawk-page-subtitle mt-0.5 text-sm leading-tight">{t('dataPool.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <Database className="w-3.5 h-3.5 text-slate-500" />
              {isLoadingItems ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
              ) : (
                <span>{t('common.articlesToday', { count: dateItems.length })}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="w-full min-w-0 flex-1 overflow-hidden px-1 pb-2 sm:pb-3">
        <div className="flex h-full min-h-0 flex-col gap-4">

        {/* ── Toolbar ── */}
        <div className="flex shrink-0 items-center justify-between gap-4">

          {/* Left: date nav + date picker */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setSelectedDate(dateKeys[currentIdx + 1]); setSearch(''); setTopicFilter(null); }}
              disabled={!canGoOlder}
              className="w-8 h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="text-center">
              <p className="text-sm font-semibold text-slate-800 leading-none">
                {isLoadingDates ? '…' : formatDateLabel(selectedDate, todayKey, yesterdayKey, t, localeTag)}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">{t('common.articles', { count: dateItems.length })}</p>
            </div>

            <button
              type="button"
              onClick={() => { setSelectedDate(dateKeys[currentIdx - 1]); setSearch(''); setTopicFilter(null); }}
              disabled={!canGoNewer}
              className="w-8 h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Date picker */}
            <input
              type="date"
              value={selectedDate}
              max={todayKey}
              onChange={(e) => handleDateInput(e.target.value)}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 text-slate-600 cursor-pointer"
            />
          </div>

          {/* Right: search bar with topic dropdown */}
          <div className="relative" ref={searchWrapperRef}>
            {/* Input row */}
            <div
              className={`flex items-center gap-1.5 border rounded-lg bg-white px-2.5 py-1.5 w-72 transition-all ${
                dropdownOpen ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-300'
              }`}
            >
              <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />

              {/* Active topic chip */}
              {topicFilter && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${TOPIC_COLORS[topicFilter] ?? 'bg-white/10 text-slate-400'}`}>
                  {topicFilter}
                  <button
                    type="button"
                    onClick={() => setTopicFilter(null)}
                    className="opacity-60 hover:opacity-100"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              )}

              <input
                type="text"
                placeholder={topicFilter ? t('dataPool.searchWithTopic') : t('dataPool.searchOrFilter')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setDropdownOpen(true)}
                className="flex-1 text-sm bg-transparent focus:outline-none min-w-0"
              />

              {(search || topicFilter) && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setTopicFilter(null); }}
                  className="text-gray-300 hover:text-slate-500 transition-colors flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Topic dropdown */}
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute top-full mt-1.5 right-0 w-72 bg-white border border-slate-200 rounded-xl z-50 p-3"
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{t('dataPool.filterByTopic')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {allTopics.map((topic) => (
                      <button
                        key={topic}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                        onClick={() => selectTopic(topic)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                          topicFilter === topic
                            ? (TOPIC_COLORS[topic] ?? 'bg-white/10 text-slate-300') + ' border-transparent'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Article table ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDate}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="min-h-0 flex-1"
          >
            <Card className="flex min-h-0 h-full flex-col border-slate-200 shadow-none">
              <CardContent className="min-h-0 flex-1 p-0">
                {isLoadingItems ? (
                  <div className="flex h-full min-h-[50vh] items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                  </div>
                ) : displayed.length === 0 ? (
                  <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-2 text-slate-500">
                    <FileText className="w-8 h-8" />
                    <p className="text-sm">
                      {dateItems.length === 0
                        ? t('dataPool.emptyDate')
                        : t('dataPool.emptyFilters')}
                    </p>
                  </div>
                ) : (
                  <div className="flex h-full min-h-0 flex-col">
                    {/* Single table with sticky header */}
                    <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto">
                      <table className="w-full text-sm">
                        <colgroup>
                          <col className="w-[34%]" />
                          <col className="w-[18%]" />
                          <col className="w-[16%]" />
                          <col className="w-[10%]" />
                          <col className="w-[8%]" />
                          <col className="w-[14%]" />
                        </colgroup>
                        <thead className="sticky top-0 z-10">
                          <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                            <th className="px-6 py-3 font-medium">{t('dataPool.colTitle')}</th>
                            <th className="px-4 py-3 font-medium">{t('dataPool.colUrl')}</th>
                            <th className="px-4 py-3 font-medium">{t('dataPool.colTopic')}</th>
                            <th className="px-4 py-3 font-medium">{t('dataPool.colTime')}</th>
                            <th className="px-4 py-3 font-medium text-center">{t('dataPool.colDataroom')}</th>
                            <th className="px-6 py-3 font-medium" />
                          </tr>
                        </thead>
                        <tbody>
                          {displayed.map((row, i) => (
                            <motion.tr
                              key={row.id}
                              initial={{ opacity: 0, x: -4 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.025 }}
                              className="border-t border-slate-200/70 transition-colors"
                            >
                              <td className="px-6 py-3 font-medium text-slate-800 max-w-sm">
                                <span className="line-clamp-2 leading-snug">{row.title}</span>
                              </td>
                              <td className="px-4 py-3 text-slate-500 whitespace-nowrap max-w-[380px] truncate">
                                {row.url ? (
                                  <a
                                    href={
                                      isImportDocumentLink(row.url)
                                        ? '#'
                                        : row.url.startsWith('http')
                                          ? row.url
                                          : `https://${row.url}`
                                    }
                                    target={isImportDocumentLink(row.url) ? undefined : '_blank'}
                                    rel="noopener noreferrer"
                                    title={
                                      isImportDocumentLink(row.url)
                                        ? `Download: ${row.title}`
                                        : row.url
                                    }
                                    className="text-sky-700 hover:underline"
                                    onClick={async (e) => {
                                      if (!isImportDocumentLink(row.url || '')) return;
                                      e.preventDefault();
                                      try {
                                        await openImportDocument(row.url!, navigate);
                                      } catch (err) {
                                        showError(formatModeratorApiKeyError(err));
                                      }
                                    }}
                                  >
                                    {isImportDocumentLink(row.url)
                                      ? (row.title?.trim()
                                          ? `${row.title.trim()} (document)`
                                          : `Imported document #${parseImportDocumentId(row.url)}`)
                                      : row.url.replace(/^https?:\/\//, '')}
                                  </a>
                                ) : (
                                  row.source
                                )}
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <TopicTagsOverflow
                                  topics={row.topics}
                                  maxVisible={2}
                                  moreLabel={(count) => t('dataPool.moreTopics', { count })}
                                  classNameForTopic={topicChipClass}
                                />
                              </td>
                              <td className="px-4 py-3 text-slate-500 text-xs font-mono whitespace-nowrap">
                                {formatTime(row.collectedAt, localeTag)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {dataroomIds.has(Number(row.id)) ? (
                                  <span
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-green-200 bg-green-50 text-green-600"
                                    title={t('dataPool.alreadyInDataroom')}
                                    aria-label={t('dataPool.alreadyInDataroom')}
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                    title={t('dataPool.addToDataroomTitle')}
                                    aria-label={t('dataPool.addToDataroom')}
                                    onClick={() => handleAddToDataroom(row)}
                                  >
                                    <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                                  </button>
                                )}
                              </td>
                              <td className="px-6 py-3">
                                <Link
                                  to="/article-analysis"
                                  state={{
                                    articleId: row.id,
                                    sourceUrl: row.url,
                                    scrapedDate: selectedDate,
                                  }}
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  {t('common.open')}
                                </Link>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Bottom fade hint — only shown when list is long enough to scroll */}
                      {displayed.length > 8 && (
                        <div className="sticky bottom-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                      )}
                    </div>
                    {/* end single-table wrapper */}

                    {/* Footer: row count */}
                    <div className="px-6 py-2.5 border-t border-white/5 flex items-center justify-between">
                      <p className="text-xs text-slate-500">
                        {displayed.length === dateItems.length
                          ? t('common.articles', { count: displayed.length })
                          : t('common.articlesOf', { shown: displayed.length, total: dateItems.length })}
                      </p>
                      {(search.trim() || topicFilter) && (
                        <button
                          type="button"
                          onClick={() => { setSearch(''); setTopicFilter(null); }}
                          className="text-xs text-slate-500 hover:text-slate-400 flex items-center gap-1 transition-colors"
                        >
                          <X className="w-3 h-3" /> {t('common.clearFilters')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
